import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Download, Minus, Plus, X, Send, CheckCircle2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useApp } from "../context/AppContext";
import { API, api, errMsg, useFetch } from "../lib/api";
import { DAYS_LETTER, longDate, monthGrid, shortDay, thisMonth, todayISO, timeAgo } from "../lib/fmt";
import { PageHeader, Loading, Empty, Segmented, MonthNav, Section } from "../components/bits";
import { AddToCalendar } from "../components/AddToCalendar";

const covered = (d) => d.people.length >= d.needed;
const flagged = (d) => !covered(d) && d.date >= todayISO();

function ManagerDay({ day, team, onClose, onDone }) {
  const { sp, user } = useApp();
  if (!day) return null;
  const t = team?.days.find((x) => x.date === day.date);
  const assigned = new Set(day.people.map((p) => p.user_id));
  const call = async (path, body, msg) => {
    try { await api.post(sp(`/days/${day.date}/${path}`), body); if (msg) toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-3xl bg-white" data-testid="day-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="text-xl font-bold">{longDate(day.date)}</SheetTitle>
          <p className={covered(day) ? "font-semibold text-emerald-700" : "font-semibold text-red-600"} data-testid="day-coverage">{day.people.length}/{day.needed} {covered(day) ? "· couvert" : "· à couvrir"}</p>
        </SheetHeader>
        <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2">
          <span className="text-sm font-semibold">Postes à couvrir</span>
          <div className="flex items-center gap-3">
            <button className="rounded-lg border bg-white p-2" onClick={() => call("needed", { needed: Math.max(0, day.needed - 1) })} data-testid="day-needed-minus" aria-label="Moins"><Minus className="h-4 w-4" /></button>
            <span className="w-5 text-center font-bold" data-testid="day-needed-value">{day.needed}</span>
            <button className="rounded-lg border bg-white p-2" onClick={() => call("needed", { needed: day.needed + 1 })} data-testid="day-needed-plus" aria-label="Plus"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
        {day.people.length > 0 && <h3 className="eyebrow mb-2 mt-5">Affectées</h3>}
        <div className="space-y-2">
          {day.people.map((p) => (
            <div key={p.user_id} className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid={`day-assigned-${p.user_id}`}>
              <span className="font-bold text-emerald-900">✓ {p.name}{p.replacement_id ? " · 🔁 remplacement demandé" : ""}</span>
              <button onClick={() => call("unassign", { user_id: p.user_id }, "Affectation retirée")} className="rounded-lg p-2 text-emerald-800" aria-label="Retirer" data-testid="day-unassign-btn"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <h3 className="eyebrow mb-2 mt-5">Disponibles</h3>
        <div className="space-y-2">
          {(t?.dispo || []).filter((p) => !assigned.has(p.user_id)).map((p) => (
            <button key={p.user_id} onClick={() => call("assign", { user_id: p.user_id }, `${p.name.split(" ")[0]} affectée ✓`)} data-testid={`day-assign-${p.user_id}`}
              className="tap flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 text-left font-semibold">
              <span>🟢 {p.name}{p.user_id === user.id ? " (moi)" : ""}</span><span className="text-sm text-teal-600">Affecter</span>
            </button>
          ))}
          {t && t.dispo.filter((p) => !assigned.has(p.user_id)).length === 0 && <p className="text-sm text-slate-500">Personne d'autre n'est disponible.</p>}
        </div>
        {t && (t.indispo.length > 0 || t.noresp.length > 0) && (
          <div className="mt-4 space-y-1 text-sm text-slate-500">
            {t.indispo.length > 0 && <p>🔴 Indisponibles : {t.indispo.join(", ")}</p>}
            {t.noresp.length > 0 && <p>⚪ Sans réponse : {t.noresp.join(", ")}</p>}
          </div>
        )}
        <div className="h-4" />
      </SheetContent>
    </Sheet>
  );
}

function NurseDay({ day, tour, onClose, onDone }) {
  const { sp } = useApp();
  const [reason, setReason] = useState("");
  if (!day) return null;
  const me = day.people.find((p) => p.mine);
  const past = day.date <= todayISO();
  const ask = async () => {
    try { const { data } = await api.post(sp(`/days/${day.date}/replacement`), { reason }); toast.success("Demande envoyée"); onDone(data.id); } catch (e) { toast.error(errMsg(e)); }
  };
  const done = async () => { try { await api.post(sp(`/days/${day.date}/done`), { done: !me.done }); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl bg-white" data-testid="nurse-day-sheet">
        <SheetHeader className="text-left"><SheetTitle className="text-xl font-bold">{longDate(day.date)}</SheetTitle>
          <p className="text-slate-500">Tournée{tour.start ? ` · ${tour.start}–${tour.end}` : ""}</p></SheetHeader>
        <div className="mt-5 grid gap-3 pb-4">
          <AddToCalendar title={`Tournée – ${tour.name}`} date={day.date} start={tour.start} end={tour.end} query={`day=${day.date}`} />
          {past && <button className={me.done ? "btn-primary" : "btn-ghost"} onClick={done} data-testid="day-done-btn">{me.done ? "✓ Journée réalisée" : "Confirmer comme réalisée"}</button>}
          {!past && (me.replacement_id ? (
            <Link to={`/missions/${me.replacement_id}`} className="btn-soft" data-testid="day-replacement-open">🔁 Remplacement demandé · voir</Link>
          ) : (
            <div className="space-y-2 rounded-2xl bg-slate-50 p-3">
              <input className="field" placeholder="Motif (facultatif, sans info médicale)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="replacement-reason-input" />
              <button className="btn-danger w-full" onClick={ask} data-testid="replacement-request-btn">Demander un remplacement</button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DayRow({ d, isManager, tour, onTap }) {
  const { wd, n } = shortDay(d.date);
  const me = d.people.find((p) => p.mine);
  return (
    <button onClick={onTap} className={`card tap flex w-full gap-4 px-4 py-3 text-left ${d.date === todayISO() ? "border-teal-400" : ""}`} data-testid={`planning-day-${d.date}`}>
      <div className="w-11 shrink-0"><div className="text-xs font-bold text-slate-400">{wd}</div><div className="text-2xl font-bold text-teal-900">{n}</div></div>
      <div className="min-w-0 flex-1 space-y-1 py-1">
        {isManager ? (
          <>
            {d.people.length > 0 && <div className="font-bold text-emerald-700">{d.people.map((p) => p.name.split(" ")[0] + (p.replacement_id ? " 🔁" : "")).join(", ")} ✓</div>}
            {flagged(d) && <div className="font-bold text-red-600" data-testid="day-uncovered">⚠️ À couvrir{d.needed > 1 ? ` (${d.people.length}/${d.needed})` : ""}</div>}
          </>
        ) : me && <div className="font-bold text-emerald-700">Tournée ✓{me.done ? " · réalisée" : ""}{me.replacement_id ? " · 🔁" : ""}</div>}
        {d.missions.map((m) => <div key={m.id} className="truncate text-sm font-semibold text-slate-600">⚡ {m.title} · {m.start}–{m.end}{m.assigned.length ? ` · ${m.assigned.join(", ")}` : ""}</div>)}
      </div>
    </button>
  );
}

function CalendarView({ days, isManager, onTap }) {
  return (
    <div className="card p-2" data-testid="planning-calendar">
      <div className="mb-1 grid grid-cols-7 text-center text-xs font-bold text-slate-400">{DAYS_LETTER.map((l, i) => <span key={i}>{l}</span>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {monthGrid(days.map((d) => d.date)).map((ds, i) => {
          if (!ds) return <div key={i} />;
          const d = days.find((x) => x.date === ds);
          const mine = d.people.some((p) => p.mine) || d.missions.some((m) => m.mine);
          const red = isManager && flagged(d);
          const work = isManager ? d.people.length > 0 : mine;
          return (
            <button key={ds} onClick={() => onTap(d)} data-testid={`cal-day-${ds}`}
              className={`flex min-h-[62px] flex-col items-center rounded-lg border border-slate-100 p-1 ${red ? "bg-red-50 text-red-700" : work ? "bg-emerald-50 text-emerald-800" : "bg-white"} ${ds === todayISO() ? "ring-2 ring-teal-500" : ""}`}>
              <span className="text-sm font-bold">{Number(ds.slice(8))}</span>
              <span className="w-full truncate text-[10px] font-semibold">{isManager ? (d.people[0]?.name.split(" ")[0] || (red ? "⚠️" : "")) : mine ? "✓" : ""}</span>
              {d.missions.length > 0 && <span className="text-[10px]">⚡</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AllTeams({ month }) {
  const { data } = useFetch(`/me/planning?month=${month}`);
  if (!data) return <Loading />;
  if (!data.items.length) return <Empty icon={CalendarDays} text="Rien de prévu ce mois-ci." />;
  return (
    <div className="space-y-2" data-testid="all-teams-list">
      {data.items.map((i, k) => (
        <div key={k} className="card flex items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1"><div className="font-bold">{longDate(i.date)}</div>
            <div className="truncate text-sm text-slate-600">{i.kind === "day" ? "Tournée" : `⚡ ${i.title}`} · <span className="font-semibold text-teal-700">{i.team}</span></div></div>
        </div>
      ))}
    </div>
  );
}

function PublishBar({ data, month, isManager, isSupport, onDone }) {
  const { sp } = useApp();
  if (!data) return null;
  if (data.published) return <div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800" data-testid="planning-published-badge"><CheckCircle2 className="h-4 w-4" /> Publié{data.published_at ? ` le ${new Date(data.published_at).toLocaleDateString("fr-BE")}` : ""}</div>;
  if (!isManager || isSupport) return null;
  const publish = async () => {
    if (!window.confirm(`Publier le planning de ${data.label} ? Toute l'équipe sera prévenue.`)) return;
    try { await api.post(sp(`/planning/${month}/publish`)); toast.success("Planning publié"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="planning-unpublished-bar">
      <span className="flex-1 text-sm text-amber-900">Brouillon : l'équipe ne voit pas encore ce mois.</span>
      <button className="btn-primary !min-h-[42px] !px-4 text-sm" onClick={publish} data-testid="planning-publish-btn"><Send className="h-4 w-4" /> Publier le mois</button>
    </div>
  );
}

export default function Planning() {
  const { sp, isManager, isSupport, structures } = useApp();
  const [params] = useSearchParams();
  const [month, setMonth] = useState(/^\d{4}-\d{2}$/.test(params.get("m") || "") ? params.get("m") : thisMonth());
  const [view, setView] = useState("list");
  const [scope, setScope] = useState("team");
  const [day, setDay] = useState(null);
  const { data, reload } = useFetch(sp(`/planning?month=${month}`));
  const { data: team, reload: reloadTeam } = useFetch(isManager ? sp(`/avail/${month}/team`) : null);
  const visible = data ? (isManager ? data.days : data.days.filter((d) => d.people.some((p) => p.mine) || d.missions.some((m) => m.mine))) : [];
  const refresh = async () => { await reload(); reloadTeam(); };
  const openDay = (d) => {
    if (isManager) return setDay(d);
    if (d.people.some((p) => p.mine)) setDay(d);
  };
  const current = day && data ? data.days.find((x) => x.date === day.date) : null;

  return (
    <div data-testid="planning-page">
      <PageHeader title={isManager ? "Planning" : "Mon planning"} action={
        <a href={`${API}/me/ics?month=${month}`} className="btn-ghost !min-h-[40px] !px-3 !py-2 text-sm" data-testid="month-ics-link"><Download className="h-4 w-4" /> Agenda</a>} />
      <MonthNav month={month} setMonth={setMonth} label={data?.label || ""} />
      <div className="mb-4 grid gap-2">
        <Segmented options={[["list", "Liste"], ["cal", "Calendrier"]]} value={view} onChange={setView} testid="planning-view" />
        {!isManager && structures.length > 1 && <Segmented options={[["team", "Cette équipe"], ["all", "Toutes mes équipes"]]} value={scope} onChange={setScope} testid="planning-scope" />}
      </div>
      {scope !== "all" && <PublishBar data={data} month={month} isManager={isManager} isSupport={isSupport} onDone={reload} />}
      {scope === "all" && !isManager ? <AllTeams month={month} /> : !data ? <Loading /> : !isManager && !data.published ? <Empty icon={CalendarDays} text="Le planning de ce mois n'est pas encore publié par votre responsable." /> : view === "cal" ? (
        <CalendarView days={data.days} isManager={isManager} onTap={openDay} />
      ) : visible.length === 0 ? <Empty icon={CalendarDays} text="Rien de prévu ce mois-ci." /> : (
        <div className="space-y-2">{visible.map((d) => <DayRow key={d.date} d={d} isManager={isManager} tour={data.tour} onTap={() => openDay(d)} />)}</div>
      )}
      {isManager && data?.recent?.length > 0 && (
        <Section title="Changements récents">
          <div className="card divide-y divide-slate-100 text-sm">
            {data.recent.map((r) => <div key={r.id} className="flex justify-between gap-2 px-4 py-2.5"><span>{r.text}</span><span className="shrink-0 text-xs text-slate-400">{timeAgo(r.created_at)}</span></div>)}
          </div>
        </Section>
      )}
      {isManager && current && <ManagerDay day={current} team={team} onClose={() => setDay(null)} onDone={refresh} />}
      {!isManager && current && <NurseDay day={current} tour={data.tour} onClose={() => setDay(null)} onDone={() => { setDay(null); reload(); }} />}
    </div>
  );
}
